import { format, formatDistanceToNowStrict, isThisYear, isToday } from 'date-fns';
import { DateTime } from 'luxon';

export function toDate(inDate: string | Date | DateTime) {
    //take whatever input we get, and ensure it gets converted to a Date
    if (Object.prototype.toString.call(inDate) === '[object Date]') return inDate as Date;
    else if (typeof inDate == 'string') return new Date(inDate);
    else if (DateTime.isDateTime(inDate)) return inDate.toJSDate();
    else return undefined;
}

export function toRelativeTimeString(
    inDate: string | Date | DateTime,
    showMinutes: boolean = false,
    addSuffix: boolean = true,
    daysLimit: number = 5,
): string {
    const date = toDate(inDate);

    if (!date) return '';

    let daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - daysLimit);

    if (date > daysAgo)
        return formatDistanceToNowStrict(date, {
            roundingMethod: 'floor',
            addSuffix: addSuffix,
        });
    else {
        const yearFormat = date.getFullYear() !== new Date().getFullYear() ? ', yyyy' : '';
        const minsFormat = showMinutes ? ', h:mm aaaa' : '';
        return format(date, `LLL d${yearFormat}${minsFormat}`);
    }
}

export function toCleanDateTime(inDate: string | Date | DateTime): string {
    const date = toDate(inDate);

    if (!date) return '';

    if (isToday(date)) {
        return `Today at ${format(date, 'h:mm a')}`;
    } else if (isThisYear(date)) {
        return format(date, "MMM d 'at' h:mm a");
    } else {
        return format(date, "MMM d, yyyy 'at' h:mm a");
    }
}

export function formatDate(date: Date): string {
    try {
        let formatString = '';
        if (isToday(date)) {
            return 'Today';
        } else if (isThisYear(date)) {
            formatString = 'MMMM d';
        } else {
            formatString = 'MMMM d, yyyy';
        }
        return format(date, formatString);
    } catch (error: any) {
        console.error('formatDate', date, error);
        return '';
    }
}

export function formatDateTime(date: Date): string {
    try {
        let formatString = '';
        if (isToday(date)) {
            formatString = 'h:mm a';
        } else if (isThisYear(date)) {
            formatString = "MMM d '\u00B7' h:mm a";
        } else {
            formatString = "MMM d, yyyy '\u00B7' h:mm a";
        }
        return format(date, formatString);
    } catch (error: any) {
        console.error('formatDateTime', date, error);
        return '';
    }
}

export function formatDateTimeFull(date: Date): string {
    try {
        return format(date, "MMMM d, yyyy '\u00B7' h:mm a OOOO");
    } catch (error: any) {
        console.error('formatDateTimeFull', date, error);
        return '';
    }
}

export function secondsToHumanReadable(seconds: number): string {
    if (seconds < 0) {
        return seconds.toString(); //just print negative
    }

    let remainingSeconds = seconds;

    const days = Math.floor(remainingSeconds / (60 * 60 * 24));
    remainingSeconds %= 60 * 60 * 24;

    const hours = Math.floor(remainingSeconds / (60 * 60));
    remainingSeconds %= 60 * 60;

    const minutes = Math.floor(remainingSeconds / 60);
    remainingSeconds %= 60;

    let output = '';

    if (days) {
        output += `${days} day${days !== 1 ? 's' : ''} `;
    }
    if (hours) {
        output += `${hours} hour${hours !== 1 ? 's' : ''} `;
    }
    if (minutes) {
        output += `${minutes} min${minutes !== 1 ? 's' : ''} `;
    }
    if (remainingSeconds) {
        output += `${remainingSeconds} sec${remainingSeconds !== 1 ? 's' : ''}`;
    }

    return output.trim();
}
